/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import session from '../../../../share/session';
import error from '../../../../common/nodejs/errors';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  WAIT: 'wait',
  CONFIRM: 'confirm',
  SUCCESS: 'success',
  FAILED: 'failed',
  CONNECT_FAILED: 'connectionFailed'
};

export default {
  name: 'requirement020',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      isFirst: true,
      // ダイアログフラグ
      modal_work_support_020: false
    };
  },
  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.CONNECT_FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
          return 'warning';
        case PHASE.WAIT:
        case PHASE.CONFIRM:
        default:
          return 'secondary';
      }
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_work_support_020 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
    },

    /** 待機画面でOKボタンクリック */
    clickConfirm() {
      // 確認表示画面へ遷移
      this.phase = PHASE.CONFIRM;
    },

    /** 実行処理 */
    async execute() {
      try {
        // 実行監視画面へ遷移
        this.phase = PHASE.WAIT;    
        this.isLoading = true;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute',
            is_first : this.isFirst
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：停止画面へ遷移
              this.phase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
              // 異常終了
              switch (response.data.async_code) {
                case error.WSP_ROUTINE_FAILED_ERROR:
                  // ルーチン実行、ルーチン結果確認で異常終了の場合、再実行確認画面へ遷移
                  this.phase = PHASE.FAILED;
                  break;
                default:
                  // 該当のエラーコードが存在しない場合、汎用エラー
                  this.showUnexpectedError();
                  return;
              }
              break;
          }
        }
      } finally {
        this.isLoading = false;
        this.isFirst = false;
      }
    },

    /**
     * Exit処理
     * @param {*} intError 0:エラー遷移あり, 1:エラー遷移なし
     */
    async exit(intError) {
      this.isLoading = true;
      try {
        this.isLoading = true;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (intError === 0) {
          // エラー遷移あり
          if (response) {
            // 正常コールバック
            switch (response.data.status) {
              case config.STATUS_WSP_COMPLETED:
                // 正常終了：機能終了
                this.closeWizard();
                break;
              case config.STATUS_WSP_FAILED:
              default:
                // 異常終了：汎用エラー
                this.showUnexpectedError();
                return;
            }
          }
        } else {
          // エラー遷移なし：機能終了
          this.closeWizard();
          return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    async showUnexpectedError() {
      // エラー画面に遷移する
      this.phase = PHASE.CONNECT_FAILED;
    },

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result handler.validate で返ってきたバリデーション結果
    */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        // result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 画面を閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_work_support_020 = false;
    }
  },
  mounted() {
    this.init();
  }
};